// Pure indexing helpers for scheduled tasks on the calendar. Mirrors the
// job assignment fan-out (person×day + by-date) but tasks are SINGLE-DAY, so
// there's no multi-day expansion. Kept pure + separate for unit testing.

import type { CalendarTaskRow } from '@gutterinv/shared';
import { NO_PEOPLE_ROW_ID } from '../components/CalendarWeekView.js';

/** Index tasks by `${personId}:${taskDate}`. A task with N people lands in N
 *  buckets (fan-out); a task with zero people lands in the synthetic
 *  NO_PEOPLE_ROW bucket so it never disappears. */
export function indexTasksByPersonDay(tasks: CalendarTaskRow[]): Map<string, CalendarTaskRow[]> {
  const idx = new Map<string, CalendarTaskRow[]>();
  for (const t of tasks) {
    const personIds = t.people.length > 0 ? t.people.map((p) => p.id) : [NO_PEOPLE_ROW_ID];
    for (const pid of personIds) {
      const key = `${pid}:${t.taskDate}`;
      const list = idx.get(key);
      if (list) list.push(t);
      else idx.set(key, [t]);
    }
  }
  return idx;
}

/** Index tasks by their single day (month view). */
export function indexTasksByDate(tasks: CalendarTaskRow[]): Map<string, CalendarTaskRow[]> {
  const idx = new Map<string, CalendarTaskRow[]>();
  for (const t of tasks) {
    const list = idx.get(t.taskDate);
    if (list) list.push(t);
    else idx.set(t.taskDate, [t]);
  }
  return idx;
}

/** True if any task has no people — drives the "(no people yet)" swimlane. */
export function anyTaskUnassigned(tasks: CalendarTaskRow[]): boolean {
  return tasks.some((t) => t.people.length === 0);
}

/** New assignee set when a task is dropped on a week-view PERSON row, or null
 *  when the people shouldn't change (date-only move). A SINGLE-assignee task
 *  dropped on a DIFFERENT real person row reassigns to that person (drag
 *  up/down = reassign). Multi-person tasks (don't collapse the fan-out),
 *  zero-person/parked tasks, the synthetic "(no people yet)" row, and a drop
 *  on the same row all return null. */
export function taskDropReassignTo(
  sourcePersonIds: string[],
  targetPersonId: string,
  isNoPeopleRow: boolean,
): string[] | null {
  if (isNoPeopleRow) return null;
  if (sourcePersonIds.length !== 1) return null;
  if (sourcePersonIds[0] === targetPersonId) return null;
  return [targetPersonId];
}
